import { Monitor as MonitorIcon, Star } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { categoryDef } from '@/lib/constants';
import type { Monitor } from '@/lib/api';

const CATEGORY_BADGE_STYLE: Record<string, string> = {
  focal: 'bg-cat-focal/10 text-cat-focal',
  oled: 'bg-cat-oled/10 text-cat-oled',
  gaming: 'bg-cat-gaming/10 text-cat-gaming',
  graphic: 'bg-cat-graphic/10 text-cat-graphic',
  curved: 'bg-cat-curved/10 text-cat-curved',
  popular: 'bg-cat-popular/10 text-cat-popular',
  portable: 'bg-cat-portable/10 text-cat-portable',
};

export interface SkuCardProps {
  monitor: Monitor;
  onSelect?: (m: Monitor) => void;
}

export function SkuCard({ monitor: m, onSelect }: SkuCardProps) {
  const cat = categoryDef(m.category);
  const CatIcon = cat?.icon ?? MonitorIcon;

  const specs = [
    m.size ? `${m.size}"` : null,
    m.resolution,
    m.refresh_rate ? `${m.refresh_rate}Hz` : null,
    m.panel_type,
  ].filter(Boolean) as string[];

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={() => onSelect?.(m)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect?.(m);
        }
      }}
      className="group flex cursor-pointer flex-col overflow-hidden transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {/* Thumb */}
      <div className="relative aspect-video bg-gradient-to-br from-muted to-secondary">
        {m.thumb_url ? (
          <img src={m.thumb_url} alt={m.name} loading="lazy" className="h-full w-full object-contain" />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-1.5 text-muted-foreground">
            <MonitorIcon className="h-9 w-9 opacity-50" strokeWidth={1.25} />
            <span className="text-[10px] font-semibold uppercase tracking-wider">{m.brand}</span>
          </div>
        )}
        <div className="absolute left-2 top-2 flex flex-wrap gap-1">
          {Boolean(m.is_focal) && (
            <Badge
              variant="secondary"
              className="h-5 gap-0.5 bg-cat-focal/10 px-1.5 py-0 text-[10px] text-cat-focal"
            >
              <Star className="h-2.5 w-2.5 fill-current" />
              Focal
            </Badge>
          )}
          {m.category && (
            <Badge
              variant="secondary"
              className={cn(
                'h-5 gap-0.5 px-1.5 py-0 text-[10px]',
                CATEGORY_BADGE_STYLE[m.category] ?? 'bg-secondary text-secondary-foreground'
              )}
            >
              <CatIcon className="h-2.5 w-2.5" />
              {cat?.label ?? m.category}
            </Badge>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col gap-1.5 p-3">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{m.id}</div>
        <div className="line-clamp-2 min-h-[2.5rem] text-[13px] font-medium leading-tight" title={m.name}>
          {m.name}
        </div>
        <div className="mt-auto flex flex-wrap gap-1 pt-1">
          {specs.map((s) => (
            <span
              key={s}
              className="rounded bg-muted px-1.5 py-0.5 text-[11px] tabular-nums text-muted-foreground"
            >
              {s}
            </span>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between border-t bg-muted/30 px-3 py-2">
        <div className="text-xs font-semibold">{m.brand}</div>
        {m.node_raw && (
          <div className="ml-2 truncate text-[10px] text-muted-foreground" title={m.node_raw}>
            {m.node_raw}
          </div>
        )}
      </div>
    </Card>
  );
}
